<!--
  Tutors' requests waiting for your OK (a lesson or a student with no market price listed): approve
  at the pay you set for that student and tutor, or decline. Shown on Students and the Calendar.
  `studentId` narrows it to one student.
-->
<script lang="ts">
  import { pairingPay } from "@split/core";
  import { fill, shows } from "../../settings/index.ts";
  import * as TT from "../../settings/tutoring.ts";
  import { tutoring } from "../../lib/tutoring.svelte.ts";
  import { centsInput, readMoney } from "../../lib/format.ts";
  import { app } from "../../lib/store.svelte.ts";

  let { studentId = null }: { studentId?: string | null } = $props();

  const pending = $derived(tutoring.claims.filter((c) => c.status === "pending" && (!studentId || c.studentId === studentId)));
  const name = (list: { id: string; name: string }[], id: string | null) => list.find((x) => x.id === id)?.name ?? "";
  const when = (dt: string) =>
    `${new Date(`${dt.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" })} ${dt.slice(11, 16)}`;
  function what(c: (typeof pending)[number]) {
    const who = name(tutoring.students, c.studentId);
    if (c.kind === "student") return fill(TT.REQUEST_STUDENT_WHAT, { name: who });
    const s = tutoring.slots.find((x) => x.id === c.slotId);
    return s ? `${who}, ${when(s.startsAt)}` : who;
  }
  /** The pay box starts at what you've set for this pair, or the market price, or the default. */
  let pay = $state<Record<string, string>>({});
  const payOf = (c: (typeof pending)[number]) =>
    pay[c.id] ?? centsInput(tutoring.students.find((s) => s.id === c.studentId)?.marketPayCents || pairingPay(c.studentId, c.tutorId, tutoring.pairings));
  async function approve(c: (typeof pending)[number]) {
    const cents = readMoney(payOf(c));
    if (cents === null) return app.toast(TT.REQUEST_PAY_LABEL, "error");
    await tutoring.decideClaim(c.id, true, cents, TT.REQUEST_APPROVED_TOAST);
  }
</script>

{#if pending.length}
  <section class="reqs" aria-label={TT.REQUESTS_TITLE}>
    {#if shows(TT.REQUESTS_TITLE) && !studentId}<h2>{TT.REQUESTS_TITLE}</h2>{/if}
    {#each pending as c (c.id)}
      <div class="req">
        <span class="what">{fill(TT.REQUEST_LINE, { tutor: name(tutoring.tutors, c.tutorId), what: what(c) })}</span>
        <label class="field pay"><span>{TT.REQUEST_PAY_LABEL}</span><input class="input money" inputmode="decimal" value={payOf(c)} oninput={(e) => (pay[c.id] = (e.currentTarget as HTMLInputElement).value)} /></label>
        <span class="btns">
          <button class="btn small primary" onclick={() => approve(c)}>{TT.REQUEST_APPROVE}</button>
          <button class="btn small quiet" onclick={() => tutoring.decideClaim(c.id, false, null, TT.REQUEST_DECLINED_TOAST)}>{TT.REQUEST_DECLINE}</button>
        </span>
      </div>
    {/each}
  </section>
{/if}

<style>
  .reqs {
    display: grid;
    gap: 8px;
    padding: 14px 16px;
    margin-bottom: 14px;
    border-radius: var(--r-lg);
    background: var(--surface);
    box-shadow: inset 0 0 0 2px var(--focus);
  }

  h2 {
    margin: 0;
    font-size: var(--t-md);
  }

  .req {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 12px;
    align-items: flex-end;
    padding-top: 8px;
    border-top: 1px solid var(--rule);
  }

  .what {
    flex: 1 1 240px;
    align-self: center;
  }

  .pay {
    flex: 0 0 110px;
  }

  .btns {
    display: flex;
    gap: 6px;
  }
</style>
